<template>
  <div class="role-management">
    <div v-if="isUserAdmin" class="access-denied">
      <el-result
        icon="warning"
        title="Akses Ditolak"
        subTitle="Anda tidak memiliki akses untuk mengelola role sistem."
      >
      </el-result>
    </div>
    
    <div v-else>
      <!-- Header -->
      <div class="page-header">
        <div class="header-content">
          <h1 class="page-title">
            <i class="el-icon-s-tools"></i>
            Manajemen Role & User
          </h1>
          <p class="page-subtitle">Kelola role sistem dan hak akses pengguna</p>
        </div>
      </div>

      <!-- Tabs Container -->
      <div class="table-section">
        <el-tabs v-model="activeTab" type="card" @tab-click="handleTabClick">
          <!-- Tab 1: Daftar Role Sistem -->
          <el-tab-pane label="Master Role" name="roles">
            <template slot="label">
              <span><i class="el-icon-s-tools"></i> Master Role</span>
            </template>
            
            <div class="tab-header">
              <el-input
                v-model="roleSearchQuery"
                placeholder="Cari role..."
                prefix-icon="el-icon-search"
                size="small"
                clearable
                style="width: 250px; margin-right: 10px;"
                @input="handleRoleSearch"
              />
              <el-button size="small" type="primary" icon="el-icon-plus" @click="showCreateDialog">
                Tambah Role Baru
              </el-button>
            </div>

            <!-- Role Table -->
            <el-table
              :data="paginatedRoles"
              style="width: 100%"
              v-loading="loading"
              element-loading-text="Memuat data role..."
            >
              <el-table-column prop="roleCode" label="Kode Role" width="150">
              </el-table-column>
              <el-table-column prop="roleName" label="Nama Role" width="200">
              </el-table-column>
              <el-table-column prop="description" label="Deskripsi">
              </el-table-column>
              <el-table-column prop="enabled" label="Status" width="120" align="center">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.enabled ? 'success' : 'danger'">
                    {{ scope.row.enabled ? 'Aktif' : 'Tidak Aktif' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Aksi" width="330" align="center">
                <template slot-scope="scope">
                  <div class="flex">
                  <el-button
                    size="mini"
                    type="primary"
                    icon="el-icon-edit"
                    @click="showEditDialog(scope.row)"
                  >
                    Edit
                  </el-button>
                  <el-button
                    size="mini"
                    :type="scope.row.enabled ? 'warning' : 'success'"
                    :icon="scope.row.enabled ? 'el-icon-close' : 'el-icon-check'"
                    @click="toggleRoleStatus(scope.row)"
                  >
                    {{ scope.row.enabled ? 'Non-aktifkan' : 'Aktifkan' }}
                  </el-button>
                  <el-button
                    size="mini"
                    type="danger"
                    icon="el-icon-delete"
                    @click="confirmDeleteRole(scope.row)"
                  >
                    Hapus
                  </el-button>
                  </div>
                </template>
              </el-table-column>
            </el-table>

            <!-- Role Pagination -->
            <div class="pagination-container">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="currentPage"
                :page-sizes="[10, 20, 50, 100]"
                :page-size="pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="filteredRoles.length"
              >
              </el-pagination>
            </div>
          </el-tab-pane>

          <!-- Tab 2: Manajemen Role User -->
          <el-tab-pane label="Manajemen Role User" name="user-roles">
            <template slot="label">
              <span><i class="el-icon-user"></i> Manajemen Role User</span>
            </template>
            
            <div class="tab-header">
              <el-input
                v-model="userRoleSearchQuery"
                placeholder="Cari username..."
                prefix-icon="el-icon-search"
                size="small"
                clearable
                style="width: 250px; margin-right: 10px;"
                @input="handleUserRoleSearch"
              />
              <el-button size="small" type="success" icon="el-icon-plus" @click="showAssignDialog">
                Assign Role ke User
              </el-button>
            </div>

            <!-- User Role Table -->
            <el-table
              :data="paginatedUserRoles"
              style="width: 100%"
              v-loading="loadingUserRoles"
              element-loading-text="Memuat data user roles..."
            >
              <el-table-column prop="username" label="Username" width="200">
              </el-table-column>
              <el-table-column prop="roleName" label="Roles" min-width="250">
                <template slot-scope="scope">
                  <div class="roles-container">
                    <el-tag 
                      v-for="role in scope.row.roles" 
                      :key="role"
                      :type="getRoleTagType(role)"
                      size="small"
                      
                    >
                      {{ role }}
                    </el-tag>
                    <!-- <p v-for="role in scope.row.roles" :key="role" :class="getRoleTagType(role)" class="text-xs p-0 mx-0.5 my-0">{{ role }}</p> -->
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="assignedAt" label="Tanggal Assign" width="180">
                <template slot-scope="scope">
                  {{ formatDate(scope.row.assignedAt) }}
                </template>
              </el-table-column>
              <el-table-column prop="status" label="Status" width="120" align="center">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.status === 'active' ? 'success' : 'danger'">
                    {{ scope.row.status === 'active' ? 'Aktif' : 'Tidak Aktif' }}
                  </el-tag>
                  <!-- <p class="text-xs p-0 m-0" :class="scope.row.status === 'active' ? 'text-green-500' : 'text-red-500'">{{ scope.row.status === 'active' ? 'Aktif' : 'Tidak Aktif' }}</p> -->
                </template>
              </el-table-column>
              <el-table-column label="Aksi" width="320" align="center">
                <template slot-scope="scope">
                  <div class="flex">
                  <el-button
                    size="mini"
                    type="primary"
                    icon="el-icon-edit"
                    @click="showEditAssignDialog(scope.row)"
                  >
                    Edit
                  </el-button>
                  <el-button
                    size="mini"
                    :type="scope.row.status === 'active' ? 'warning' : 'success'"
                    :icon="scope.row.status === 'active' ? 'el-icon-close' : 'el-icon-check'"
                    @click="toggleUserRoleStatus(scope.row)"
                  >
                    {{ scope.row.status === 'active' ? 'Nonaktifkan' : 'Aktifkan' }}
                  </el-button>
                  <el-button
                    size="mini"
                    type="danger"
                    icon="el-icon-delete"
                    @click="confirmRemoveUserRole(scope.row)"
                  >
                    Hapus
                  </el-button>
                  </div>
                </template>
              </el-table-column>
            </el-table>

            <!-- User Role Pagination -->
            <div class="pagination-container">
              <el-pagination
                @size-change="handleUserRoleSizeChange"
                @current-change="handleUserRoleCurrentChange"
                :current-page="userRoleCurrentPage"
                :page-sizes="[10, 20, 50, 100]"
                :page-size="userRolePageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="filteredUserRoles.length"
              >
              </el-pagination>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- Create/Edit Role Dialog -->
      <el-dialog 
        :title="isEditing ? 'Edit Role' : 'Tambah Role Baru'"
        :visible.sync="showRoleDialog"
        width="500px"
      >
        <el-form :model="roleForm" :rules="roleRules" ref="roleForm">
          <el-form-item label="Kode Role" prop="roleCode">
            <el-input 
              v-model="roleForm.roleCode" 
              placeholder="Masukkan kode role (contoh: ROLE_MANAGER)"
              :disabled="isEditing"
            ></el-input>
          </el-form-item>
          <el-form-item label="Nama Role" prop="roleName">
            <el-input 
              v-model="roleForm.roleName" 
              placeholder="Masukkan nama role (contoh: Manager)"
            ></el-input>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="showRoleDialog = false">Batal</el-button>
          <el-button type="primary" @click="saveRole" :loading="saving">
            {{ isEditing ? 'Update' : 'Simpan' }}
          </el-button>
        </div>
      </el-dialog>

      <!-- Assign/Edit User Role Dialog -->
      <el-dialog 
        :title="isEditingUserRole ? 'Edit Role User' : 'Assign Role ke User'"
        :visible.sync="showUserRoleDialog"
        width="500px"
      >
        <el-form :model="userRoleForm" :rules="userRoleRules" ref="userRoleForm">
          <el-form-item label="Username" prop="username">
            <el-input 
              v-model="userRoleForm.username" 
              placeholder="Masukkan username"
              :disabled="isEditingUserRole"
            ></el-input>
          </el-form-item>
          <el-form-item label="Role" prop="roleNames">
            <el-select 
              v-model="userRoleForm.roleNames" 
              multiple
              placeholder="Pilih satu atau lebih role" 
              style="width: 100%"
              collapse-tags
            >
              <el-option
                v-for="role in roles"
                :key="role.roleCode"
                :label="role.roleName"
                :value="role.roleName"
              >
              </el-option>
            </el-select>
            <div class="form-help-text">
              Anda dapat memilih lebih dari satu role untuk user ini
            </div>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click="showUserRoleDialog = false">Batal</el-button>
          <el-button type="primary" @click="saveUserRole" :loading="savingUserRole">
            {{ isEditingUserRole ? 'Update' : 'Assign' }}
          </el-button>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RoleManagementHome',
  data() {
    return {
      // Tab Management
      activeTab: 'roles',
      
      // Search queries
      roleSearchQuery: '',
      userRoleSearchQuery: '',
      
      loading: false,
      saving: false,
      loadingUserRoles: false,
      savingUserRole: false,
      currentPage: 1,
      pageSize: 10,
      userRoleCurrentPage: 1,
      userRolePageSize: 10,
      showRoleDialog: false,
      showUserRoleDialog: false,
      isEditing: false,
      isEditingUserRole: false,
      roleForm: {
        roleCode: '',
        roleName: ''
      },
      userRoleForm: {
        id: null,
        username: '',
        roleNames: []
      },
      roleRules: {
        roleCode: [
          { required: true, message: 'Kode role wajib diisi', trigger: 'blur' },
          { min: 3, max: 50, message: 'Kode role harus 3-50 karakter', trigger: 'blur' },
          { pattern: /^ROLE_[A-Z_]+$/, message: 'Format kode role harus dimulai dengan ROLE_ dan huruf kapital', trigger: 'blur' }
        ],
        roleName: [
          { required: true, message: 'Nama role wajib diisi', trigger: 'blur' },
          { min: 3, max: 50, message: 'Nama role harus 3-50 karakter', trigger: 'blur' }
        ]
      },
      userRoleRules: {
        username: [
          { required: true, message: 'Username wajib diisi', trigger: 'blur' },
          { min: 3, max: 30, message: 'Username harus 3-30 karakter', trigger: 'blur' },
          { pattern: /^[a-zA-Z0-9_]+$/, message: 'Username hanya boleh huruf, angka, dan underscore', trigger: 'blur' }
        ],
        roleNames: [
          { type: 'array', required: true, message: 'Minimal satu role harus dipilih', trigger: 'change' },
          { type: 'array', min: 1, message: 'Minimal satu role harus dipilih', trigger: 'change' }
        ]
      },
      // Data dummy untuk tabel sementara
      roles: [
        {
          roleCode: 'ROLE_ADMIN',
          roleName: 'Administrator',
          description: 'Full system access',
          enabled: true
        },
        {
          roleCode: 'ROLE_MANAGER',
          roleName: 'Manager',
          description: 'Management access',
          enabled: true
        },
        {
          roleCode: 'ROLE_USER',
          roleName: 'User',
          description: 'Basic user access',
          enabled: true
        }
      ],
      // Data dummy untuk user roles
      userRoles: [
        {
          id: 1,
          username: 'admin123',
          roles: ['Administrator', 'Manager'],
          assignedAt: new Date('2024-01-15T09:00:00'),
          status: 'active'
        },
        {
          id: 2,
          username: 'manager_john',
          roles: ['Manager', 'User'],
          assignedAt: new Date('2024-01-20T10:30:00'),
          status: 'active'
        },
        {
          id: 3,
          username: 'user_jane',
          roles: ['User'],
          assignedAt: new Date('2024-01-25T14:15:00'),
          status: 'active'
        },
        {
          id: 4,
          username: 'supervisor_bob',
          roles: ['Administrator', 'Manager', 'User'],
          assignedAt: new Date('2024-02-01T08:45:00'),
          status: 'inactive'
        },
        {
          id: 5,
          username: 'operator_alice',
          roles: ['User'],
          assignedAt: new Date('2024-02-05T16:20:00'),
          status: 'active'
        }
      ]
    }
  },
  computed: {
    isUserAdmin() {
      // Sementara set ke false untuk testing, bisa diubah sesuai kebutuhan
      return false
    },
    filteredRoles() {
      let filtered = this.roles || []
      
      // Apply search filter
      if (this.roleSearchQuery) {
        const query = this.roleSearchQuery.toLowerCase()
        filtered = filtered.filter(role => 
          role.roleCode.toLowerCase().includes(query) ||
          role.roleName.toLowerCase().includes(query) ||
          role.description.toLowerCase().includes(query)
        )
      }
      
      return filtered
    },
    paginatedRoles() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.filteredRoles.slice(start, end)
    },
    filteredUserRoles() {
      let filtered = this.userRoles || []
      
      // Apply search filter
      if (this.userRoleSearchQuery) {
        const query = this.userRoleSearchQuery.toLowerCase()
        filtered = filtered.filter(userRole => 
          userRole.username.toLowerCase().includes(query) ||
          userRole.roles.some(role => role.toLowerCase().includes(query))
        )
      }
      
      return filtered
    },
    paginatedUserRoles() {
      const start = (this.userRoleCurrentPage - 1) * this.userRolePageSize
      const end = start + this.userRolePageSize
      return this.filteredUserRoles.slice(start, end)
    }
  },
  async created() {
    if (!this.isUserAdmin) {
      await this.loadRoles()
    }
  },
  methods: {
    // Tab Management
    handleTabClick(tab) {
      this.activeTab = tab.name;
      // Optional: Add any additional logic when switching tabs
      if (tab.name === 'roles') {
        // Refresh roles data if needed
        console.log('Switched to roles tab');
      } else if (tab.name === 'user-roles') {
        // Refresh user roles data if needed
        console.log('Switched to user roles tab');
      }
    },
    
    // Search Methods
    handleRoleSearch() {
      // Reset to first page when searching
      this.currentPage = 1;
    },
    
    handleUserRoleSearch() {
      // Reset to first page when searching
      this.userRoleCurrentPage = 1;
    },
    
    // API Configuration
    getApiConfig() {
      return {
        baseURL: 'https://28eecac0-a680-4183-98ca-a3840977e7cf.mock.pstmn.io/api',
        headers: {
          'Content-Type': 'application/json',
        }
      }
    },

    // API Call untuk POST role
    async createRoleAPI(roleData) {
      try {
        const config = this.getApiConfig()
        const response = await fetch(`${config.baseURL}/roles`, {
          method: 'POST',
          headers: config.headers,
          body: JSON.stringify(roleData)
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        return result
      } catch (error) {
        console.error('API Error:', error)
        throw error
      }
    },
    
    async loadRoles() {
      this.loading = true
      try {
        // Simulasi load data - bisa diganti dengan API call jika ada endpoint GET
        await new Promise(resolve => setTimeout(resolve, 500))
        console.log('Roles loaded successfully')
      } catch (error) {
        this.$message.error('Gagal memuat data role: ' + error.message)
      } finally {
        this.loading = false
      }
    },

    showCreateDialog() {
      this.isEditing = false
      this.roleForm = {
        roleCode: '',
        roleName: ''
      }
      this.showRoleDialog = true
      this.$nextTick(() => {
        this.$refs.roleForm && this.$refs.roleForm.clearValidate()
      })
    },

    showEditDialog(role) {
      this.isEditing = true
      this.roleForm = {
        roleCode: role.roleCode,
        roleName: role.roleName
      }
      this.showRoleDialog = true
      this.$nextTick(() => {
        this.$refs.roleForm && this.$refs.roleForm.clearValidate()
      })
    },

    async saveRole() {
      try {
        const isValid = await this.$refs.roleForm.validate()
        if (!isValid) return

        this.saving = true
        
        if (this.isEditing) {
          // Update role locally (bisa ditambahkan API call untuk update)
          const index = this.roles.findIndex(r => r.roleCode === this.roleForm.roleCode)
          if (index !== -1) {
            this.roles[index] = { ...this.roles[index], ...this.roleForm }
          }
          this.$message.success('Role berhasil diupdate')
        } else {
          // Create new role via API
          const response = await this.createRoleAPI({
            roleCode: this.roleForm.roleCode,
            roleName: this.roleForm.roleName
          })
          
          // Add to local data
          this.roles.push({
            roleCode: this.roleForm.roleCode,
            roleName: this.roleForm.roleName,
            description: `Role for ${this.roleForm.roleName}`,
            enabled: true
          })
          
          this.$message.success('Role berhasil dibuat dan dikirim ke API')
          console.log('API Response:', response)
        }
        
        this.showRoleDialog = false
      } catch (error) {
        this.$message.error('Gagal menyimpan role: ' + error.message)
        console.error('Save role error:', error)
      } finally {
        this.saving = false
      }
    },

    async toggleRoleStatus(role) {
      try {
        // Update status locally
        const index = this.roles.findIndex(r => r.roleCode === role.roleCode)
        if (index !== -1) {
          this.roles[index].enabled = !this.roles[index].enabled
        }
        this.$message.success(`Role berhasil ${role.enabled ? 'dinonaktifkan' : 'diaktifkan'}`)
      } catch (error) {
        this.$message.error('Gagal mengubah status role: ' + error.message)
      }
    },

    confirmDeleteRole(role) {
      this.$confirm(
        `Apakah Anda yakin ingin menghapus role "${role.roleName}"? Tindakan ini tidak dapat dibatalkan.`,
        'Konfirmasi Hapus',
        {
          confirmButtonText: 'Ya, Hapus',
          cancelButtonText: 'Batal',
          type: 'warning'
        }
      ).then(async () => {
        try {
          // Remove from local data
          const index = this.roles.findIndex(r => r.roleCode === role.roleCode)
          if (index !== -1) {
            this.roles.splice(index, 1)
          }
          this.$message.success('Role berhasil dihapus')
        } catch (error) {
          this.$message.error('Gagal menghapus role: ' + error.message)
        }
      }).catch(() => {
        // User cancelled
      })
    },

    async assignRole() {
      if (!this.userRoleForm.userId || !this.userRoleForm.roleName) {
        this.$message.warning('User ID dan Role harus diisi')
        return
      }
      
      try {
        // Simulasi assign role
        this.$message.success('Role berhasil di-assign ke user')
        this.userRoleForm = { userId: '', roleName: '' }
      } catch (error) {
        this.$message.error('Gagal assign role: ' + error.message)
      }
    },

    async removeRole() {
      if (!this.userRoleForm.userId || !this.userRoleForm.roleName) {
        this.$message.warning('User ID dan Role harus diisi')
        return
      }
      
      try {
        // Simulasi remove role
        this.$message.success('Role berhasil dihapus dari user')
        this.userRoleForm = { userId: '', roleName: '' }
      } catch (error) {
        this.$message.error('Gagal remove role: ' + error.message)
      }
    },

    async getUserRoles() {
      if (!this.userRoleForm.userId) {
        this.$message.warning('User ID harus diisi')
        return
      }
      
      try {
        // Simulasi get user roles
        const dummyRoles = ['ROLE_USER', 'ROLE_MANAGER']
        this.$message.info(`User memiliki role: ${dummyRoles.join(', ')}`)
      } catch (error) {
        this.$message.error('Gagal mengambil role user: ' + error.message)
      }
    },

    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },

    handleCurrentChange(val) {
      this.currentPage = val
    },

    // User Role Management Methods
    handleUserRoleSizeChange(val) {
      this.userRolePageSize = val
      this.userRoleCurrentPage = 1
    },

    handleUserRoleCurrentChange(val) {
      this.userRoleCurrentPage = val
    },

    showAssignDialog() {
      this.isEditingUserRole = false
      this.userRoleForm = {
        id: null,
        username: '',
        roleNames: []
      }
      this.showUserRoleDialog = true
      this.$nextTick(() => {
        this.$refs.userRoleForm && this.$refs.userRoleForm.clearValidate()
      })
    },

    showEditAssignDialog(userRole) {
      this.isEditingUserRole = true
      this.userRoleForm = {
        id: userRole.id,
        username: userRole.username,
        roleNames: [...userRole.roles] // Copy array roles
      }
      this.showUserRoleDialog = true
      this.$nextTick(() => {
        this.$refs.userRoleForm && this.$refs.userRoleForm.clearValidate()
      })
    },

    async saveUserRole() {
      try {
        const isValid = await this.$refs.userRoleForm.validate()
        if (!isValid) return

        this.savingUserRole = true

        if (this.isEditingUserRole) {
          // Update existing user role
          const index = this.userRoles.findIndex(ur => ur.id === this.userRoleForm.id)
          if (index !== -1) {
            this.userRoles[index] = {
              ...this.userRoles[index],
              roles: [...this.userRoleForm.roleNames]
            }
          }
          this.$message.success('Role user berhasil diupdate')
        } else {
          // Check if username already exists
          const existingUser = this.userRoles.find(ur => 
            ur.username === this.userRoleForm.username &&
            ur.status === 'active'
          )
          
          if (existingUser) {
            // User exists, merge roles
            const newRoles = [...new Set([...existingUser.roles, ...this.userRoleForm.roleNames])]
            const index = this.userRoles.findIndex(ur => ur.id === existingUser.id)
            if (index !== -1) {
              this.userRoles[index].roles = newRoles
            }
            this.$message.success(`Role berhasil ditambahkan ke user ${this.userRoleForm.username}`)
          } else {
            // Create new user role assignment
            const newUserRole = {
              id: Math.max(...this.userRoles.map(ur => ur.id)) + 1,
              username: this.userRoleForm.username,
              roles: [...this.userRoleForm.roleNames],
              assignedAt: new Date(),
              status: 'active'
            }
            
            this.userRoles.unshift(newUserRole)
            this.$message.success('Role berhasil di-assign ke user')
          }
        }

        this.showUserRoleDialog = false
      } catch (error) {
        this.$message.error('Gagal menyimpan user role: ' + error.message)
        console.error('Save user role error:', error)
      } finally {
        this.savingUserRole = false
      }
    },

    toggleUserRoleStatus(userRole) {
      try {
        const index = this.userRoles.findIndex(ur => ur.id === userRole.id)
        if (index !== -1) {
          this.userRoles[index].status = this.userRoles[index].status === 'active' ? 'inactive' : 'active'
        }
        this.$message.success(`Status role user berhasil ${userRole.status === 'active' ? 'dinonaktifkan' : 'diaktifkan'}`)
      } catch (error) {
        this.$message.error('Gagal mengubah status role user: ' + error.message)
      }
    },

    confirmRemoveUserRole(userRole) {
      this.$confirm(
        `Apakah Anda yakin ingin menghapus semua role dari user "${userRole.username}"?`,
        'Konfirmasi Hapus',
        {
          confirmButtonText: 'Ya, Hapus',
          cancelButtonText: 'Batal',
          type: 'warning'
        }
      ).then(() => {
        try {
          const index = this.userRoles.findIndex(ur => ur.id === userRole.id)
          if (index !== -1) {
            this.userRoles.splice(index, 1)
          }
          this.$message.success('Role user berhasil dihapus')
        } catch (error) {
          this.$message.error('Gagal menghapus role user: ' + error.message)
        }
      }).catch(() => {
        // User cancelled
      })
    },

    // Utility Methods
    getRoleTagType(roleName) {
      const typeMap = {
        'Administrator': 'danger',
        'Manager': 'warning',
        'User': 'primary'
      }
      return typeMap[roleName] || ''
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
/* Page Layout */
.role-management {
  padding: 24px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.role-management-container {
  padding: 24px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.access-denied {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

/* Header Section */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 32px;
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
}

.header-content {
  color: white;
}

.header-section {
  text-align: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.page-title i {
  font-size: 36px;
}

.page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}

.page-description {
  font-size: 1.1rem;
  color: #7f8c8d;
  margin: 0;
}

/* Table Section */
.table-section {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
}

.content-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  margin-bottom: 20px;
}

/* Custom Table Styling */
.el-table {
  border-radius: 8px;
  overflow: hidden;
}

.el-table th {
  background-color: #f8fafc !important;
  font-weight: 600;
  color: #2d3748;
  border-bottom: 2px solid #e2e8f0;
}

.el-table td {
  padding: 16px 0;
}

.el-table .el-table__row:hover > td {
  background-color: #f7fafc !important;
}

/* Tab Styling */
.content-card .el-tabs--card > .el-tabs__header {
  margin: 0;
  border-bottom: 1px solid #e4e7ed;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.content-card .el-tabs--card > .el-tabs__header .el-tabs__nav {
  border: none;
  border-radius: 0;
}

.content-card .el-tabs--card > .el-tabs__header .el-tabs__item {
  border: none;
  background: transparent;
  color: #606266;
  font-weight: 500;
  padding: 15px 25px;
  transition: all 0.3s ease;
  border-radius: 0;
}

.content-card .el-tabs--card > .el-tabs__header .el-tabs__item:hover {
  color: #409EFF;
  background: rgba(64, 158, 255, 0.1);
}

.content-card .el-tabs--card > .el-tabs__header .el-tabs__item.is-active {
  background: linear-gradient(135deg, #409EFF 0%, #5dade2 100%);
  color: white;
  border-bottom: 3px solid #409EFF;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  /* padding: 20px 20px 0 20px; */
}

.tab-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
}

.el-tabs__content {
  padding: 0 20px 20px 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #2c3e50;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.dialog-footer {
  text-align: right;
}

/* Multiple Roles Styling */
.roles-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.role-tag {
  margin: 2px;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
}

/* Form Help Text */
.form-help-text {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  line-height: 1.4;
  font-style: italic;
}

/* Multiple Select Styling */
.el-select--multiple .el-select__tags {
  max-height: 100px;
  overflow-y: auto;
}

.el-select--multiple .el-tag--info {
  background-color: #409eff;
  border-color: #409eff;
  color: white;
}

.el-select--multiple .el-tag--info .el-tag__close {
  color: white;
}

/* Responsive tab styling */
@media (max-width: 768px) {
  .role-management,
  .role-management-container {
    padding: 16px;
  }
  
  .page-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
    padding: 24px;
  }
  
  .tab-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
    padding: 15px 15px 0 15px;
  }
  
  .content-card .el-tabs--card > .el-tabs__header .el-tabs__item {
    padding: 12px 15px;
    font-size: 14px;
  }
  
  .el-tabs__content {
    padding: 0 15px 15px 15px;
  }
  
  .table-section,
  .content-card {
    padding: 16px;
  }
}

.el-select--multiple .el-tag--info .el-tag__close:hover {
  background-color: rgba(255, 255, 255, 0.2);
}
</style>
